import classNames from "classnames";
import BlockCards from "components/shared/BlockCards";
import DateRangeSelectors from "components/shared/DateRangeSelectors";
import { useDateSelectorShortcuts } from "components/shared/DateRangeSelectors/utils";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import { SmallBody, SmallBodyHighlight } from "helpers/TypographyComponents";
import { isObjectEmpty } from "helpers/utils";
import useMounted from "hooks/useMounted";
import { useEffect, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import type { AnalyticsChartType, DashboardData } from "store/analyticsStore";
import { fetchDashboardData } from "store/analyticsStore";
import { putCompanySettings } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";
import type { IDatesRange } from "types";

import LoadingBlurFilter from "../LoadingBlurFilter";
import type { StoredPreferences } from "../utils";
import { ANALYTICS_DATE_PICKER_MAX_END, blockCardsDataTransform, hydrateDates, settingsTransform } from "../utils";
import styles from "./style.module.scss";

interface IIndicators {
    /**
     * Dashboard data
     */
    dashboard: DashboardData | undefined;
    /**
     * Stored preferences for filters
     */
    storedPreferences: StoredPreferences;
    /**
     * Data type
     */
    type: AnalyticsChartType;
    /**
     * Dates range for the period
     */
    periodsDateRange: IDatesRange;
    /**
     * Show "vs N-1" label when there is multiple periods
     */
    showVsN_1Label: boolean;
    /**
     * Fetch data status
     */
    fetchStatus: TFetchStatus;
}
const Indicators = ({
    dashboard,
    storedPreferences,
    type,
    periodsDateRange,
    showVsN_1Label,
    fetchStatus,
}: IIndicators) => {
    const mounted = useMounted(1000);

    const dispatch = useAppDispatch();
    const { chosenDates: storedDates } = storedPreferences?.storedFilters?.[type] || {};
    const isLoading = fetchStatus !== "succeeded" && !dashboard;
    const { periodsShortcuts } = useDateSelectorShortcuts();

    const { refreshAnalyticsStatus } = useAppSelector((state) => state.analytics);

    const [chosenDates, setChosenDates] = useState<IDatesRange>(hydrateDates(storedDates) || periodsDateRange);

    useEffect(() => {
        if (periodsDateRange?.startDate && isObjectEmpty(chosenDates)) {
            setChosenDates(periodsDateRange);
        }
    }, [periodsDateRange]);

    const blockCardsData = blockCardsDataTransform(dashboard, !showVsN_1Label);
    const { t } = useTranslation();

    useEffect(() => {
        if (isObjectEmpty(chosenDates) || !chosenDates) return;

        dispatch(fetchDashboardData({ dates: chosenDates, type }));

        const preferences = { chosenDates };
        const settingsValue = settingsTransform("filters", preferences, storedPreferences, type);
        dispatch(putCompanySettings([{ settingsName: "analytics_layout", settingsValue }]));
    }, [chosenDates]);

    return (
        <>
            {/** GRAPH LOADING FILTER */}
            {isLoading && <LoadingBlurFilter />}

            {/** HEADER */}
            <div className={classNames({ [styles["indicators-header"]]: true, [styles["loading"]]: isLoading })}>
                <div className={styles["indicators-label"]}>
                    <SmallBodyHighlight>{t("Analytics.IndicatorsN")}</SmallBodyHighlight>{" "}
                    {showVsN_1Label && <SmallBody color="grey">vs N-1</SmallBody>}
                </div>
                <div className={styles["date-range-filters"]}>
                    {!isObjectEmpty(chosenDates) && (
                        <DateRangeSelectors
                            label={t("Analytics.DataBetween")}
                            dateRange={periodsDateRange}
                            maxEndDate={ANALYTICS_DATE_PICKER_MAX_END}
                            defaultValues={chosenDates}
                            handleChanges={(newDatesRange) => setChosenDates(newDatesRange as IDatesRange)}
                            rangeShortcuts={periodsShortcuts}
                        />
                    )}
                </div>
            </div>

            {/** BODY */}
            <ScrollShadowWrapper style={{ height: "100%" }}>
                <div className={styles["block-card-wrapper"]}>
                    <BlockCards
                        data={blockCardsData![0]}
                        compact
                        borderTop
                        className={styles["analytics-block-card"]}
                        isLoading={isLoading}
                        showEvolution={false} // It's not possible yet to compare released titles
                    />
                    <BlockCards
                        data={blockCardsData![1]}
                        compact
                        borderTop
                        className={styles["analytics-block-card"]}
                        isLoading={isLoading}
                        showEvolution={showVsN_1Label}
                    />
                    <BlockCards
                        data={blockCardsData![2]}
                        compact
                        borderTop
                        className={styles["analytics-block-card"]}
                        isLoading={isLoading}
                        showEvolution={showVsN_1Label}
                    />
                </div>
            </ScrollShadowWrapper>
        </>
    );
};

export default React.memo(Indicators);
